import * as m from 'mithril';
import AppContext from '../context/AppContext';

import BusLayoutPage from '../pages/seatselection/BusLayoutPage';
import { SubFsm } from 'little-fsm';
import { ApiService } from '../ApiService';
import { InternationalStringDTO, SeatDTO, SeatSelectionDTO, TripCustomizationDTO } from '../apiclient/ApiDtos';
import { SeatReservationFsmScheme } from '../states/seatreservation/SeatReservationFsmScheme';
import SeatAssignmentAutoInfoPage from '../pages/seatselection/SeatAssignmentAutoInfoPage';

export type SearchStatus = 'none' | 'pending' | 'success';

export function buildSeatReservationPresenter(
    appContext: AppContext,
    apiService: ApiService,
    subFsm: SubFsm<SeatReservationFsmScheme>): { render: () => m.Vnode } {

    let messageProvider = appContext.getMessageProvider();

    let seatSelections: SeatDTO[] = [];
    let isEditingSeatSelection = false;
    let shouldWarnSeatUnchangeable: boolean = false;


    subFsm.setEnterEffect("fetching_trip_details", (state) => {
        if (state.tripCustomization && state.selectedSeats.length > 0) {
            isEditingSeatSelection = true;
        } else {
            isEditingSeatSelection = false;
        }
        seatSelections = state.selectedSeats;
        let tripResult = state.tripSelectedContext.selectedTrip;
        if (state.errorMessage == null && state.tripDetails) {
            subFsm.processEvent('trip_details_already_reveived', {})
        } else if (state.tripSelectedContext.selectedTrip.isSeatSelectionAllowed
            || state.tripSelectedContext.selectedTrip.hasPickUpService
            || state.tripSelectedContext.selectedTrip.hasDropOffService
        ) {
            apiService.getTrip(tripResult.id).then(result => {
                if (result.right) {
                    console.debug("Got Seat Plan: ");
                    subFsm.processEvent('trip_details_received', { tripDetails: result.right })
                    m.redraw();
                }

            });
        } else {
            subFsm.processEvent('trip_details_received', { tripDetails: state.tripSelectedContext.selectedTrip })
        }

    });

    subFsm.setEnterEffect("selecting_seatplan_or_auto_assignment", (state) => {
        if (state.tripDetails && state.tripDetails.isSeatSelectionAllowed && state.tripDetails.seatPlan) {
            subFsm.processEvent('seatplan_mode_determined', {})
        } else {
            subFsm.processEvent('seats_auto_assignment_mode_determined', {})
        }
    });


    subFsm.setEnterEffect("showing_seat_reservation_error", (state) => {
        subFsm.processEvent('seatplan_refetching_requested', {})
    });


    subFsm.setEnterEffect("reserving_seats", (state) => {
        let tripResult = state.tripSelectedContext.selectedTrip;
        let seats = state.selectedSeats;

        apiService.createTripCustimazion(tripResult.id, state.tripSelectedContext.tripSearchContext.searchCriteria.numberOfSeats, seats, null, null)
            .then(errorOrResult => {
                if (errorOrResult.right) {
                    let tripCustomization: TripCustomizationDTO = errorOrResult.right;
                    subFsm.processEvent('seat_reservation_created', { tripCustomization: tripCustomization });
                } else {
                    let errorMessage: InternationalStringDTO = {
                        en: messageProvider.get(
                            tripResult.isSeatSelectionAllowed ? "seat_not_availiable" : "failed_to_request_seat_order",
                            null, "en"),
                        my: messageProvider.get(
                            tripResult.isSeatSelectionAllowed ? "seat_not_availiable" : "failed_to_request_seat_order",
                            null, "my")
                    };
                    subFsm.processEvent('seat_reservation_failed', { errorMessage: errorMessage });
                }
            });
    });


    return {
        render() {
            let context = subFsm.getCurrentContext();
            let substate = subFsm.getCurrentSubState();

            let numberOfSeats = context.tripSelectedContext.tripSearchContext.searchCriteria.numberOfSeats;

            let requiredSeatSelection: SeatDTO[] = [];

            let addToSeatSelections = (seat) => {
                seatSelections.push(seat);

                for (let i = 0; i < requiredSeatSelection.length; i++) {
                    if (requiredSeatSelection[i] == null) {
                        requiredSeatSelection[i] = seat;
                        break;
                    }
                }

            };

            let removeFromSeatSelections = (seat) => {
                for (let i = 0; i < seatSelections.length; i++) {
                    let cursoredSeat = seatSelections[i];
                    if (seat.number == cursoredSeat.number) {
                        seatSelections.splice(i, 1);
                    }
                }

            };

            return (substate == 'showing_auto_assignment_info_view' || (substate == 'reserving_seats' && context.selectedSeats.length == 0)) ?
                m(SeatAssignmentAutoInfoPage, {
                    appContext: appContext,
                    searchCriteria: context.tripSelectedContext.tripSearchContext.searchCriteria,
                    isSubmittingSeatReservation: substate == 'reserving_seats',
                    tripResult: context.tripSelectedContext.tripSearchContext.selectedTripSearchResult!,
                    tripDetails: context.tripDetails ? context.tripDetails : null,
                    messageProvider: appContext.getMessageProvider(),
                    errorMessage: context.errorMessage ? context.errorMessage : null,
                    onBackPressed: () => {
                        subFsm.processEvent('back', {});
                    },
                    onConfirmSeatOrder: () => {
                        if (context.tripCustomization && context.tripCustomization.id != null
                            && context.tripCustomization.id.trim() != "" && !context.tripDetails?.isSeatSelectionAllowed) {
                            subFsm.processEvent('continue', {})
                        } else {
                            subFsm.processEvent('seat_reservation_requested', { selectedSeats: [] })
                        }

                    }
                })
                :
                m(BusLayoutPage, {
                    appContext: appContext,
                    shouldWarnSeatUnchangeable: shouldWarnSeatUnchangeable,
                    hidePriceInfoPopAnimation: isEditingSeatSelection,
                    searchCriteria: context.tripSelectedContext.tripSearchContext.searchCriteria,
                    seatSelections: seatSelections,
                    isSubmittingSeatReservation: substate == 'reserving_seats',
                    messageProvider: appContext.getMessageProvider(),
                    tripResult: context.tripSelectedContext.tripSearchContext.selectedTripSearchResult!,
                    tripDetails: context.tripDetails ? context.tripDetails : null,
                    errorMessage: context.errorMessage ? context.errorMessage : null,
                    onBackPressed: () => {
                        shouldWarnSeatUnchangeable = false;
                        subFsm.processEvent('back', {});
                    },
                    onSeatReservationRequested: (selectedSeats: SeatDTO[]) => {
                        if (context.tripCustomization && isEditingSeatSelection) {
                            if (areSeatSelectionsEqual(context.tripCustomization.seatOrder.seatSelections, selectedSeats)) {
                                shouldWarnSeatUnchangeable = false;
                                subFsm.processEvent('continue', {})
                            } else {
                                subFsm.processEvent('seat_reservation_requested', { selectedSeats: selectedSeats })
                            }
                        } else {
                            subFsm.processEvent('seat_reservation_requested', { selectedSeats: selectedSeats })
                        }
                    },
                    onSelectSeat: (col: any) => {
                        addToSeatSelections(col)
                    },
                    onUnselectSeat: (col: any) => {
                        if(isEditingSeatSelection){
                            shouldWarnSeatUnchangeable = true;
                        }else if (substate != 'reserving_seats') {
                            removeFromSeatSelections(col)
                        }
                    }
                })
        }
    }
}


function areSeatSelectionsEqual(
    a: SeatSelectionDTO[],
    b: SeatDTO[]
): boolean {

    const sortFn = (x: { number: string }, y: { number: string }) =>
        x.number.localeCompare(y.number);

    const sortedA = [...a].sort(sortFn);
    const sortedB = [...b].sort(sortFn);
    console.info("sortedA : ", sortedA)
    console.info("sortedB : ", sortedB)

    for (let i = 0; i < sortedA.length; i++) {
        if (sortedA[i].number.trim() !== sortedB[i].number.trim()) {
            console.debug("Changed Seat Selection. ", sortedA, sortedB)
            return false;
        }
    }

    return true;
}